import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { buildVastuEnquiryUrl } from "@/data/constants";

const GOLD = "#B89047";
const CHARCOAL = "#1A1A1A";
const OFF_WHITE = "#FDFCF8";
const BORDER = "#E5E0D8";

const PROPERTY_TYPES = ["Residential", "Commercial", "Industrial", "Other"];
const FORMATS = ["On-Site Visit", "Digital / Online"];

const inputStyle = {
  width: "100%", padding: "12px 14px", border: `1px solid ${BORDER}`,
  backgroundColor: OFF_WHITE, fontFamily: "'Outfit', sans-serif",
  fontSize: "0.88rem", color: CHARCOAL, outline: "none", borderRadius: 0,
};

const selectStyle = {
  ...inputStyle,
  appearance: "none", cursor: "pointer", paddingRight: "36px",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' fill='none' stroke-width='1.5'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center",
};

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: "block", fontFamily: "'Outfit', sans-serif", fontSize: "0.62rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#8A8A8A", fontWeight: 600, marginBottom: "8px" }}>{label}</label>
      {children}
    </div>
  );
}

export default function VastuEnquiryForm() {
  const [form, setForm] = useState({ location: "", propertyType: "", size: "", requirement: "", format: "", dates: "" });
  const update = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    window.open(buildVastuEnquiryUrl(form), "_blank");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
      {/* Form */}
      <div className="lg:col-span-3">
        <form data-testid="vastu-enquiry-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" style={{ marginBottom: "20px" }}>
            <Field label="Project Location *">
              <input data-testid="enquiry-location" required value={form.location} onChange={update("location")} placeholder="City, State / Country" style={inputStyle} onFocus={(e) => (e.target.style.borderColor = GOLD)} onBlur={(e) => (e.target.style.borderColor = BORDER)} />
            </Field>
            <Field label="Property Type *">
              <select data-testid="enquiry-property-type" required value={form.propertyType} onChange={update("propertyType")} style={selectStyle}>
                <option value="">Select type</option>
                {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Size (if applicable)">
              <input data-testid="enquiry-size" value={form.size} onChange={update("size")} placeholder="e.g. 2,000 sq ft" style={inputStyle} onFocus={(e) => (e.target.style.borderColor = GOLD)} onBlur={(e) => (e.target.style.borderColor = BORDER)} />
            </Field>
            <Field label="Preferred Format *">
              <select data-testid="enquiry-format" required value={form.format} onChange={update("format")} style={selectStyle}>
                <option value="">Select format</option>
                {FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Brief Requirement *">
            <textarea data-testid="enquiry-requirement" required value={form.requirement} onChange={update("requirement")} rows={3} placeholder="Describe your Vastu requirement briefly" style={{ ...inputStyle, resize: "vertical" }} onFocus={(e) => (e.target.style.borderColor = GOLD)} onBlur={(e) => (e.target.style.borderColor = BORDER)} />
          </Field>
          <div style={{ marginTop: "20px" }}>
            <Field label="Preferred Dates">
              <input data-testid="enquiry-dates" value={form.dates} onChange={update("dates")} placeholder="e.g. Mid-October, flexible" style={inputStyle} onFocus={(e) => (e.target.style.borderColor = GOLD)} onBlur={(e) => (e.target.style.borderColor = BORDER)} />
            </Field>
          </div>
          <button
            type="submit"
            data-testid="enquiry-submit-btn"
            style={{
              marginTop: "28px", width: "100%", backgroundColor: CHARCOAL, color: OFF_WHITE,
              padding: "15px", fontFamily: "'Outfit', sans-serif",
              fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase",
              fontWeight: 700, border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
              transition: "background-color 0.3s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = GOLD)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = CHARCOAL)}
          >
            Send Enquiry via WhatsApp <ArrowRight size={14} />
          </button>
        </form>
      </div>

      {/* Info Panel */}
      <div className="lg:col-span-2" style={{ display: "flex", flexDirection: "column", gap: "0" }}>
        {[
          { title: "How It Works", desc: "Fill in the details and send via WhatsApp. Akkshay will review and respond with availability and next steps." },
          { title: "Response Time", desc: "Enquiries are typically reviewed and responded to within 24 hours." },
          { title: "No Obligation", desc: "This is an enquiry only. No payment or commitment is required until both sides agree on scope and schedule." },
        ].map((item, i) => (
          <div key={i} style={{ padding: "24px 0", borderBottom: i < 2 ? `1px solid ${BORDER}` : "none" }}>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.05rem", fontWeight: 500, color: CHARCOAL, marginBottom: "6px" }}>{item.title}</p>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.82rem", color: "#4A4A4A", lineHeight: 1.7 }}>{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
